import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUESTIONS = [
  {
    id: 'body_frame',
    question: 'How would you describe your body frame?',
    options: [
      { label: 'Thin, lean, difficult to gain weight', value: 'vata', dosha: 'vata' },
      { label: 'Medium build, athletic, moderate weight', value: 'pitta', dosha: 'pitta' },
      { label: 'Large frame, tend to gain weight easily', value: 'kapha', dosha: 'kapha' },
    ],
  },
  {
    id: 'skin_type',
    question: 'What best describes your skin?',
    options: [
      { label: 'Dry, rough, cool to touch', value: 'vata', dosha: 'vata' },
      { label: 'Warm, oily, prone to redness/acne', value: 'pitta', dosha: 'pitta' },
      { label: 'Thick, smooth, oily, cool', value: 'kapha', dosha: 'kapha' },
    ],
  },
  {
    id: 'energy_levels',
    question: 'How are your energy levels throughout the day?',
    options: [
      { label: 'Variable, bursts of energy followed by fatigue', value: 'vata', dosha: 'vata' },
      { label: 'High, consistent, driven', value: 'pitta', dosha: 'pitta' },
      { label: 'Steady, slow to start, enduring', value: 'kapha', dosha: 'kapha' },
    ],
  },
  {
    id: 'sleep_pattern',
    question: 'What is your typical sleep pattern?',
    options: [
      { label: 'Light, interrupted, difficulty falling asleep', value: 'vata', dosha: 'vata' },
      { label: 'Moderate, wake up refreshed', value: 'pitta', dosha: 'pitta' },
      { label: 'Deep, heavy, need more sleep', value: 'kapha', dosha: 'kapha' },
    ],
  },
  {
    id: 'appetite',
    question: 'How would you describe your appetite?',
    options: [
      { label: 'Variable, often forget to eat', value: 'vata', dosha: 'vata' },
      { label: 'Strong, get irritable if meal is delayed', value: 'pitta', dosha: 'pitta' },
      { label: 'Steady, can skip meals easily', value: 'kapha', dosha: 'kapha' },
    ],
  },
  {
    id: 'digestion',
    question: 'How is your digestion?',
    options: [
      { label: 'Irregular, gas, bloating', value: 'vata', dosha: 'vata' },
      { label: 'Strong, quick, prone to heartburn', value: 'pitta', dosha: 'pitta' },
      { label: 'Slow, heavy feeling after meals', value: 'kapha', dosha: 'kapha' },
    ],
  },
  {
    id: 'stress_response',
    question: 'How do you respond to stress?',
    options: [
      { label: 'Anxious, worried, fearful', value: 'vata', dosha: 'vata' },
      { label: 'Angry, irritable, intense', value: 'pitta', dosha: 'pitta' },
      { label: 'Withdrawn, lethargic, depressed', value: 'kapha', dosha: 'kapha' },
    ],
  },
  {
    id: 'weather_preference',
    question: 'What weather do you prefer?',
    options: [
      { label: 'Warm, humid climates', value: 'vata', dosha: 'vata' },
      { label: 'Cool, moderate climates', value: 'pitta', dosha: 'pitta' },
      { label: 'Warm, dry climates', value: 'kapha', dosha: 'kapha' },
    ],
  },
];

const DoshaAssessmentPage = () => {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [personalInfo, setPersonalInfo] = useState({
    age: '',
    gender: 'other',
    location: '',
  });
  const [loading, setLoading] = useState(false);

  const isPersonalInfoStep = currentStep === 0;
  const totalSteps = QUESTIONS.length + 1;

  const handleAnswerChange = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceed = () => {
    if (isPersonalInfoStep) {
      return personalInfo.age && personalInfo.gender && personalInfo.location;
    }
    const questionId = QUESTIONS[currentStep - 1]?.id;
    return answers[questionId];
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/dosha/assess`,
        {
          user_id: user.id,
          responses: answers,
          age: parseInt(personalInfo.age),
          gender: personalInfo.gender,
          location: personalInfo.location,
        },
        { headers: getAuthHeader() }
      );

      toast.success('Assessment completed! Redirecting to your results...');
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error) {
      console.error('Assessment failed:', error);
      toast.error(error.response?.data?.detail || 'Assessment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-6 lg:px-12 py-12" data-testid="dosha-assessment-page">
        <div className="max-w-3xl mx-auto">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Step {currentStep + 1} of {totalSteps}</span>
              <span>{Math.round(((currentStep + 1) / totalSteps) * 100)}% Complete</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
              />
            </div>
          </div>

          {/* Question Card */}
          <Card className="shadow-lg">
            <CardContent className="p-8">
              {isPersonalInfoStep ? (
                <div className="space-y-6" data-testid="personal-info-step">
                  <div>
                    <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Playfair Display' }}>
                      Let's Get Started
                    </h2>
                    <p className="text-gray-600">
                      Tell us a bit about yourself to personalize your assessment
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="age">Age</Label>
                      <Input
                        id="age"
                        type="number"
                        data-testid="age-input"
                        value={personalInfo.age}
                        onChange={(e) => setPersonalInfo({ ...personalInfo, age: e.target.value })}
                        placeholder="Enter your age"
                      />
                    </div>

                    <div>
                      <Label>Gender</Label>
                      <RadioGroup
                        value={personalInfo.gender}
                        onValueChange={(value) => setPersonalInfo({ ...personalInfo, gender: value })}
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="male" id="male" data-testid="gender-male" />
                          <Label htmlFor="male">Male</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="female" id="female" data-testid="gender-female" />
                          <Label htmlFor="female">Female</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="other" id="other" data-testid="gender-other" />
                          <Label htmlFor="other">Other</Label>
                        </div>
                      </RadioGroup>
                    </div>

                    <div>
                      <Label htmlFor="location">Location / City</Label>
                      <Input
                        id="location"
                        data-testid="location-input"
                        value={personalInfo.location}
                        onChange={(e) => setPersonalInfo({ ...personalInfo, location: e.target.value })}
                        placeholder="Enter your location"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-6" data-testid={`question-step-${currentStep}`}>
                  <div>
                    <div className="text-sm text-secondary font-medium mb-2">
                      Question {currentStep} of {QUESTIONS.length}
                    </div>
                    <h2 className="text-2xl md:text-3xl font-semibold" style={{ fontFamily: 'Playfair Display' }}>
                      {QUESTIONS[currentStep - 1].question}
                    </h2>
                  </div>

                  <RadioGroup
                    value={answers[QUESTIONS[currentStep - 1].id]}
                    onValueChange={(value) => handleAnswerChange(QUESTIONS[currentStep - 1].id, value)}
                  >
                    <div className="space-y-3">
                      {QUESTIONS[currentStep - 1].options.map((option, index) => (
                        <div
                          key={index}
                          className={`border-2 rounded-xl p-4 transition-all cursor-pointer ${
                            answers[QUESTIONS[currentStep - 1].id] === option.value
                              ? 'border-primary bg-primary/5'
                              : 'border-gray-200 hover:border-primary/50'
                          }`}
                          onClick={() => handleAnswerChange(QUESTIONS[currentStep - 1].id, option.value)}
                        >
                          <div className="flex items-start space-x-3">
                            <RadioGroupItem value={option.value} id={`option-${index}`} data-testid={`option-${index}`} />
                            <Label htmlFor={`option-${index}`} className="cursor-pointer flex-1">
                              {option.label}
                            </Label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </RadioGroup>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentStep === 0}
                  data-testid="prev-button"
                >
                  <ChevronLeft className="mr-2 h-4 w-4" />
                  Previous
                </Button>

                {currentStep === totalSteps - 1 ? (
                  <Button
                    onClick={handleSubmit}
                    disabled={!canProceed() || loading}
                    className="bg-primary hover:bg-primary/90"
                    data-testid="submit-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Complete Assessment'
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    disabled={!canProceed()}
                    className="bg-primary hover:bg-primary/90"
                    data-testid="next-button"
                  >
                    Next
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Info Section */}
          <div className="mt-8 text-center text-sm text-gray-600">
            <p>
              This AI-powered assessment analyzes your responses based on traditional Ayurvedic principles to determine
              your unique Prakriti (constitution) and current Vikriti (imbalance).
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DoshaAssessmentPage;
